import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Button, Badge, Spinner, Alert, Form, Modal, ProgressBar } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faEye, faChartBar } from '@fortawesome/free-solid-svg-icons';
import { useSchool } from '../../contexts/SchoolContext';
import { useAcademicYear } from '../../contexts/AcademicYearContext';
import SchoolUserService from '../../services/schoolUser.service';

const TeacherStudents = () => {
  const { selectedSchool } = useSchool();
  const { selectedYear } = useAcademicYear();
  const [students, setStudents] = useState([]);
  const [teacherSubjects, setTeacherSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    classroom: '',
    search: ''
  });
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [attendanceStats, setAttendanceStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  useEffect(() => {
    if (selectedSchool && selectedYear) {
      fetchTeacherSubjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSchool, selectedYear]);

  useEffect(() => {
    if (teacherSubjects.length > 0) {
      fetchStudents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teacherSubjects, filters]);

  const fetchTeacherSubjects = async () => {
    try {
      // Backend filtra automaticamente baseado no papel do usuário
      const response = await SchoolUserService.getTeacherAssignments({
        school: selectedSchool,
        year: selectedYear
      });
      setTeacherSubjects(response.results || []);
    } catch (err) {
      console.error('Error fetching teacher subjects:', err);
    }
  };

  const fetchStudents = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        school: selectedSchool,
        year: selectedYear
      };

      // Aplicar filtros
      if (filters.classroom) params.classroom = filters.classroom;
      if (filters.search) params.search = filters.search;

      const response = await SchoolUserService.getTeacherStudents(params);
      setStudents(response.results || []);
    } catch (err) {
      console.error('Error fetching students:', err);
      setError('Erro ao carregar alunos.');
    } finally {
      setLoading(false);
    }
  };

  const handleView = (student) => {
    setSelectedStudent(student);
    setShowViewModal(true);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSelectedStudent(null);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      classroom: '',
      search: ''
    });
  };

  const handleViewAttendance = async (student) => {
    setShowAttendanceModal(true);
    setLoadingStats(true);
    setAttendanceStats(null);

    try {
      const stats = await SchoolUserService.getStudentAttendanceStats(student.id);
      setAttendanceStats(stats);
    } catch (err) {
      console.error('Error fetching attendance stats:', err);
      setAttendanceStats({ error: 'Erro ao carregar estatísticas de frequência.' });
    } finally {
      setLoadingStats(false);
    }
  };

  const handleCloseAttendanceModal = () => {
    setShowAttendanceModal(false);
    setAttendanceStats(null);
  };

  const getUniqueClassrooms = () => {
    const classrooms = new Map();
    teacherSubjects.forEach(ts => {
      // Criar nome completo: "Turma A - 1º Ano - Ensino Fundamental I"
      const fullName = `${ts.classroom_grade} - ${ts.classroom_education_level} - ${ts.classroom_name}`;
      if (!classrooms.has(fullName)) {
        classrooms.set(fullName, fullName);
      }
    });
    return Array.from(classrooms.values());
  };

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <h4>Meus Alunos - {selectedYear}</h4>
          <p className="mb-0">Visualize os alunos das suas turmas</p>
        </div>
      </div>

      {/* Filtros */}
      <Row className="mb-4">
        <Col xs={12}>
          <Card border="light" className="shadow-sm">
            <Card.Body>
              <Row>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Turma</Form.Label>
                    <Form.Select
                      name="classroom"
                      value={filters.classroom}
                      onChange={handleFilterChange}
                    >
                      <option value="">Todas</option>
                      {getUniqueClassrooms().map((classroom, idx) => (
                        <option key={idx} value={classroom}>
                          {classroom}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={5}>
                  <Form.Group>
                    <Form.Label>Buscar</Form.Label>
                    <Form.Control
                      type="text"
                      name="search"
                      placeholder="Nome ou matrícula..."
                      value={filters.search}
                      onChange={handleFilterChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={3} className="d-flex align-items-end">
                  <Button
                    variant="secondary"
                    onClick={handleClearFilters}
                    className="w-100"
                  >
                    Limpar
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Lista de Alunos */}
      <Row>
        <Col xs={12}>
          <Card border="light" className="shadow-sm">
            <Card.Header>
              <Row className="align-items-center">
                <Col>
                  <h5 className="mb-0">
                    <FontAwesomeIcon icon={faUsers} className="me-2" />
                    Alunos ({students.length})
                  </h5>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="p-0">
              {loading && (
                <div className="text-center py-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </Spinner>
                </div>
              )}
              {error && (
                <Alert variant="danger" className="m-3">
                  {error}
                </Alert>
              )}
              {!loading && !error && students.length === 0 && (
                <div className="text-center py-5">
                  <p className="text-muted">Nenhum aluno encontrado.</p>
                </div>
              )}
              {!loading && !error && students.length > 0 && (
                <Table responsive hover className="align-items-center table-flush mb-0">
                  <thead className="thead-light">
                    <tr>
                      <th>Matrícula</th>
                      <th>Nome</th>
                      <th>Turma</th>
                      <th>Turno</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((student) => (
                      <tr key={student.id}>
                        <td className="fw-bold">{student.registration}</td>
                        <td>{student.student_name}</td>
                        <td>{student.classroom_full_name}</td>
                        <td>
                          <Badge bg="secondary">{student.classroom_shift}</Badge>
                        </td>
                        <td className="text-center">
                          <Button
                            variant="info"
                            size="sm"
                            onClick={() => handleView(student)}
                            className="me-2"
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </Button>
                          <Button
                            variant="success"
                            size="sm"
                            onClick={() => handleViewAttendance(student)}
                          >
                            <FontAwesomeIcon icon={faChartBar} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showViewModal} onHide={handleCloseViewModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Detalhes do Aluno</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {selectedStudent && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>Nome:</strong> {selectedStudent.student_name}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Matrícula:</strong> {selectedStudent.registration}</p>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>CPF:</strong> {selectedStudent.student_cpf}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Data de Nascimento:</strong> {selectedStudent.student_birth_date ? new Date(selectedStudent.student_birth_date).toLocaleDateString('pt-BR') : '-'}</p>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>Turma:</strong> {selectedStudent.classroom_full_name}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Turno:</strong> {selectedStudent.classroom_shift}</p>
                </Col>
              </Row>
              <hr />
              <h6>Responsável</h6>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>Nome:</strong> {selectedStudent.responsible_name}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Telefone:</strong> {selectedStudent.responsible_phone}</p>
                </Col>
              </Row>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseViewModal}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showAttendanceModal} onHide={handleCloseAttendanceModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faChartBar} className="me-2" />
            Estatísticas de Frequência
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {loadingStats && (
            <div className="text-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Carregando...</span>
              </Spinner>
            </div>
          )}
          {!loadingStats && attendanceStats && attendanceStats.error && (
            <Alert variant="danger">
              {attendanceStats.error}
            </Alert>
          )}
          {!loadingStats && attendanceStats && !attendanceStats.error && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>Aluno:</strong> {attendanceStats.student_name}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Matrícula:</strong> {attendanceStats.registration}</p>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>Turma:</strong> {attendanceStats.classroom_name}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Série:</strong> {attendanceStats.classroom_grade}</p>
                </Col>
              </Row>
              <hr />
              <h6 className="mb-3">Frequência por Disciplina</h6>
              {attendanceStats.subjects && attendanceStats.subjects.length > 0 ? (
                <Table responsive hover className="mb-0">
                  <thead className="thead-light">
                    <tr>
                      <th>Disciplina</th>
                      <th className="text-center">Total de Aulas</th>
                      <th className="text-center">Presenças</th>
                      <th className="text-center">Faltas</th>
                      <th>Frequência</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendanceStats.subjects.map((subject) => (
                      <tr key={subject.subject_id}>
                        <td>{subject.subject_name}</td>
                        <td className="text-center">{subject.total_classes}</td>
                        <td className="text-center">
                          <Badge bg="success">{subject.present_count}</Badge>
                        </td>
                        <td className="text-center">
                          <Badge bg="danger">{subject.absent_count}</Badge>
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <ProgressBar
                              now={subject.attendance_percentage}
                              variant={
                                subject.attendance_percentage >= 75 ? 'success' :
                                subject.attendance_percentage >= 50 ? 'warning' : 'danger'
                              }
                              className="flex-grow-1 me-2"
                              style={{ minWidth: '100px' }}
                            />
                            <span className="fw-bold">{subject.attendance_percentage}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <Alert variant="info">
                  Nenhuma frequência registrada para este aluno ainda.
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseAttendanceModal}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TeacherStudents;
